"use client";

import Image from "next/image";
import Link from "next/link";
import { track } from "@vercel/analytics";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative border-t border-white/[0.04] px-4 py-12">
      <div className="mx-auto mb-8 flex max-w-6xl justify-center">
        <a
          href="https://www.producthunt.com/products/alpha-lab?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-alpha-lab"
          target="_blank"
          rel="noopener noreferrer"
        >
          {/* Locally-hosted Product Hunt badge SVG. Previously loaded
              from api.producthunt.com which set a Cloudflare __cf_bm
              third-party cookie on every visitor (Lighthouse Best
              Practices ding). Hosting it locally kills the cookie
              without losing the badge. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/producthunt-alpha-lab-badge.svg"
            alt="Alpha Lab™ - Stock trading sim that teaches kids real investing skills | Product Hunt"
            width={250}
            height={54}
            loading="lazy"
            decoding="async"
          />
        </a>
      </div>
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 md:flex-row md:justify-between">
        {/* Logo + name */}
        <div className="flex items-center gap-3">
          <div className="relative h-8 w-8">
            <Image
              src="/images/logo-dark.jpg"
              alt="MDG Labs"
              fill
              className="rounded-full object-cover"
              // 32px display — without sizes Next.js serves the 3840w
              // variant. Restrict to ~32px (with 2x DPI = 64px max).
              sizes="32px"
            />
          </div>
          <span className="font-[family-name:var(--font-space-grotesk)] text-sm font-semibold">
            <span className="hero-mdg-glow">MDG</span>{" "}
            <span className="text-white/90">Labs</span>
          </span>
        </div>

        {/* Links */}
        <nav className="flex flex-wrap justify-center gap-6 text-sm text-[#94A3B8]/60">
          <Link href="/#services" className="transition-colors hover:text-white/80">
            Services
          </Link>
          <Link href="/#work" className="transition-colors hover:text-white/80">
            Work
          </Link>
          <Link href="/#tech" className="transition-colors hover:text-white/80">
            Tech
          </Link>
          <Link href="/#contact" className="transition-colors hover:text-white/80">
            Contact
          </Link>
          <Link href="/about" className="transition-colors hover:text-white/80">
            About
          </Link>
          <Link
            href="/hire"
            onClick={() => track("hire_click", { location: "footer" })}
            className="transition-colors hover:text-white/80"
          >
            Hire Me
          </Link>
          {/* after-hours experiences — these live on sub-routes that used
              to be findable only from buried in-page links */}
          <Link href="/forest" className="text-emerald-300/50 transition-colors hover:text-emerald-200">
            Forest
          </Link>
          <Link href="/anatomy" className="text-emerald-300/50 transition-colors hover:text-emerald-200">
            Anatomy
          </Link>
          <Link href="/starfield" className="text-emerald-300/50 transition-colors hover:text-emerald-200">
            Deep Field
          </Link>
          <Link href="/sys" className="text-emerald-300/50 transition-colors hover:text-emerald-200">
            Fly the System
          </Link>
        </nav>

        {/* Copyright + workshop signature. The signature is the small
            artisan accent — italic, dim, attribution-style. Tells the
            reader without shouting it: every pixel on this site was
            made by one hand. */}
        <div className="flex flex-col items-center gap-1 md:items-end">
          <p className="text-xs italic text-[#94A3B8]/60 font-[family-name:var(--font-space-grotesk)]">
            made by hand — Micah
          </p>
          <p className="text-xs text-[#94A3B8]/30 font-[family-name:var(--font-jetbrains-mono)]">
            &copy; {currentYear}{" "}
            <span className="hero-mdg-glow">MDG</span>{" "}
            <span className="text-[#94A3B8]/60">Labs</span>
          </p>
        </div>
      </div>
      {/* Privacy disclosure — Vercel Analytics is privacy-friendly
          (no cookies, no fingerprinting), but a visible note is the
          right move for transparency-conscious clients. */}
      <p className="mx-auto mt-8 max-w-6xl text-center text-[10px] text-[#94A3B8]/25 font-[family-name:var(--font-jetbrains-mono)]">
        analytics: anonymous pageview counts via Vercel · no cookies · no fingerprinting
      </p>
    </footer>
  );
};

export default Footer;
